import { StudentTab } from "./StudentTab";
import {
  Datagrid,
  FunctionField,
  List,
  ReferenceField,
  useGetList,
  useGetOne,
  useGetRecordId,
  useRecordContext,
} from "react-admin";
import { DateField } from "../../fields/DateField";
import { LinkToFirestoreField } from "../../fields/LinkToFirestoreField";
import { DrivingLesson } from "../../model/autovioCalendarEvents";
import { grants } from "../../backoffice.access_control";
import { AddSimulatorDrivingLessonButton } from "../../buttons/AddSimulatorDrivingLessonButton";
import { DrivingSchool } from "../../providers/drivingSchoolsProvider";
import { Student } from "../../providers/studentsProvider";
import { Row } from "../../misc/Row";
import { ResponsiveListSurface } from "../../misc/ResponsiveListSurface";
import { useEventDialog } from "../../hooks/useEventDialog";
import { DocumentHistoricalAppointmentButton } from "../../buttons/DocumentHistoricalAppointmentButton";
import { Column } from "../../misc/Column";
import { CalendarEventStatusField } from "../../fields/CalendarEventStatusField";
import { Alert, Button, Card, Checkbox, Collapse, IconButton, Typography, useMediaQuery } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { atom, useRecoilValue, useSetRecoilState } from "recoil";
import { useThemeColor } from "../../hooks/useThemeColor";
import { CheckedIcon } from "../../icons/CheckedIcon";
import { UncheckedOutlineIcon } from "../../icons/UncheckedOutlineIcon";
import { overviewCardStyle } from "../../misc/backofficeTheme";
import * as React from "react";
import { useState } from "react";
import { LoadingButton } from "@mui/lab";
import { Form2Theme } from "../../misc/Form2";
import { FormProvider, useForm, useFormContext, useFormState, useWatch } from "react-hook-form";
import { produce } from "immer";
import { InstructorInput } from "../../inputs/InstructorInput";
import { DrivingLicenseClassInput } from "../../inputs/DrivingLicenseClassInput";
import { DrivingLessonTypeInput } from "../../inputs/DrivingLessonTypeInput";
import { StartingPointInput } from "../../inputs/StartingPointInput";
import { DrivingLessonType } from "../../model/DrivingLessonType";
import {
  DrivingLicenseClass,
  isForCar,
  isForCarOnly,
  isForCarWithTrailer,
  isForMotorcycle,
} from "../../model/DrivingLicenseClass";
import { getAuthenticatedServerClient, schemas } from "../../api/server.api";
import { LocalizedError } from "../../utils/LocalizedError";
import { z } from "zod";
import { startingPointsProvider } from "../../providers/startingPointsProvider";
import _pick from "lodash/pick";
import { VehiclesInput } from "../../inputs/VehiclesInput";
import { HiddenInput } from "../../inputs/HiddenInput";
import { EmptyState } from "../../misc/EmptyState";

const DRIVING_LESSON_TYPE_LABELS = {
  normal: "Normal",
  unterweisungAmFahrzeug: "Unterweisung am Fahrzeug",
  praktischeUnterweisung: "Praktische Unterweisung",
  ueberlandfahrt: "Überlandfahrt",
  autobahnfahrt: "Autobahnfahrt",
  nachtfahrt: "Nachtfahrt",
  simulator: "Simulator-Fahrstunde",
  praktischePruefung: "Praktische Prüfung",
  schaltkompetenz: "Schaltkompetenz",
  fahrprobe: "ASF Fahrprobe",
};

const _renderLessonType = (record: DrivingLesson): string => {
  const label = DRIVING_LESSON_TYPE_LABELS[record.drivingLessonType] ?? "Unbekannt";
  const durationInMinutes = Math.round(record.end.diff(record.start).as("minutes"));
  return `${label} (${durationInMinutes} Minuten)`;
};

export function StudentDrivingLessonsTab() {
  const student = useRecordContext<Student>();
  const { data: drivingSchool } = useGetOne<DrivingSchool>(
    "drivingSchools",
    { id: student?.drivingSchoolId },
    { enabled: !!student?.drivingSchoolId },
  );
  const [bulkEdit, setBulkEdit] = useState(false);
  const form = useForm<_BulkEditFormValues>({
    defaultValues: _bulkEditFormDefaultValues,
  });
  const setBulkEditSelection = useSetRecoilState(_bulkEditSelectionAtom);
  const { total: drivingLessonsTotal } = useGetList(
    "studentDrivingLessons",
    {
      filter: { studentUid: student?.id },
      pagination: { page: 1, perPage: 1 },
    },
    { enabled: !!student?.id },
  );
  const hasMultipleDrivingLessons = (drivingLessonsTotal ?? 0) > 1;

  return (
    <StudentTab>
      <Form2Theme>
        <FormProvider {...form}>
          <Column gap={3}>
            <Row
              sx={{
                flexDirection: { xs: "column", sm: "row" },
                flexWrap: "wrap",
                justifyContent: "space-between",
                alignItems: { xs: "stretch", sm: "center" },
                gap: "12px 16px",
                marginBottom: "9px",
              }}
            >
              <Typography sx={{ fontWeight: "bold", fontSize: 19 }}>Fahrstunden</Typography>
              {!bulkEdit && (
                <Row
                  sx={{
                    flex: { xs: "0 1 auto", sm: "1 1 425px" },
                    flexDirection: { xs: "column", sm: "row" },
                    flexWrap: "wrap",
                    justifyContent: "flex-end",
                    alignItems: { xs: "stretch", sm: "center" },
                    gap: { xs: 1.5, sm: 2 },
                    minWidth: 0,
                    "& > *": { flexShrink: 0 },
                    "& > .MuiButton-root": { width: { xs: "100%", sm: "auto" } },
                    "& .MuiButton-root": { maxWidth: "100%", whiteSpace: "normal" },
                  }}
                >
                  {hasMultipleDrivingLessons && (
                    <Button variant="outlined" onClick={() => setBulkEdit(true)}>
                      Mehrere Termine bearbeiten
                    </Button>
                  )}
                  {drivingSchool?.customizations.includes("simulatorDrivingLessons") &&
                    student?.bookedTrainings.some((it) => !it.isFinished && it.includesSimulatorDrivingLessons) && (
                      <AddSimulatorDrivingLessonButton sx={{ height: "40px" }} />
                    )}
                  {(grants.has("DrivingLesson:historical:create") || grants.has("Exam:historical:create")) && (
                    <DocumentHistoricalAppointmentButton tabs={["drivingLesson", "practicalExam"]} />
                  )}
                </Row>
              )}
            </Row>
            {bulkEdit && (
              <_BulkEditForm
                student={student}
                cancel={() => {
                  setBulkEditSelection(new Map());
                  form.reset(_bulkEditFormDefaultValues, { keepIsSubmitted: false });
                  setBulkEdit(false);
                }}
              />
            )}
            <StudentDrivingLessonsList bulkEdit={bulkEdit} />
          </Column>
        </FormProvider>
      </Form2Theme>
    </StudentTab>
  );
}

export function StudentDrivingLessonsList(props: { bulkEdit?: boolean; filter?: { bookedTrainingIds: string[] } }) {
  const { bulkEdit, filter } = props;
  const studentUid = useGetRecordId();
  const { eventDialog, openEventDialog } = useEventDialog({ syncWithUrl: true });
  const setBulkEditSelection = useSetRecoilState(_bulkEditSelectionAtom);
  return (
    <ResponsiveListSurface variant="table" sx={{ marginTop: "-15px" }}>
      <List
        className="StudentDrivingLessonsList"
        resource="studentDrivingLessons"
        title=" " // <-- prevent that the default list title is rendered
        filter={{ studentUid, ...filter }}
        sort={{ field: "start", order: "DESC" }}
        empty={<EmptyState noEntriesText="Noch keine Fahrstunden gebucht." />}
        exporter={false}
        actions={false}
        sx={{
          height: "100%",
          minWidth: 0,
          position: "relative",
          "& .RaList-main": { minWidth: 0, backgroundColor: "background.paper" },
          "& .RaList-content": { maxWidth: "100%", minWidth: 0 },
          "& .RaDatagrid-table": { minWidth: "850px", backgroundColor: "background.paper" },
        }}
      >
        <Datagrid
          bulkActionButtons={false}
          rowClick={(_, __, record) => {
            const drivingLesson = record as DrivingLesson;
            if (bulkEdit) {
              setBulkEditSelection((selectedIds) => _toggleBulkEditSelection(selectedIds, drivingLesson));
            } else {
              openEventDialog(drivingLesson);
            }
            return false;
          }}
        >
          {bulkEdit && <_Checkbox />}
          <DateField label="Datum" source="start" showTime />
          <FunctionField label="Art" sortBy="type" render={_renderLessonType} />
          <ReferenceField label="Fahrlehrer" reference="instructors" source="instructorId" />
          <ReferenceField label="Fahrzeug" reference="vehicles" source="vehicleId" />
          <CalendarEventStatusField />
          {grants.has("viewFirestore") && <LinkToFirestoreField label="" source="id" collection="/calendar_events" />}
        </Datagrid>
        {eventDialog}
      </List>
    </ResponsiveListSurface>
  );
}

const _bulkEditSelectionAtom = atom<Map</* driving lesson id */ string, /* booked training id */ string>>({
  key: "_bulkEditSelectionAtom",
  default: new Map(),
});

function _toggleBulkEditSelection(selectedIds: Map<string, string>, drivingLesson: DrivingLesson) {
  return produce(selectedIds, (draft) => {
    if (draft.has(drivingLesson.id)) {
      draft.delete(drivingLesson.id);
    } else {
      draft.set(drivingLesson.id, drivingLesson.student.bookedTrainingId ?? "");
    }
  });
}

function _Checkbox() {
  const drivingLesson = useRecordContext<DrivingLesson>();
  const bulkEditSelection = useRecoilValue(_bulkEditSelectionAtom);
  const setBulkEditSelection = useSetRecoilState(_bulkEditSelectionAtom);
  const isSelected = bulkEditSelection.has(drivingLesson.id);
  const primaryColor = useThemeColor("primary");
  const { isSubmitted } = useFormState();
  return (
    <Checkbox
      checked={isSelected}
      checkedIcon={<CheckedIcon sx={{ color: primaryColor.main }} />}
      icon={
        <UncheckedOutlineIcon
          sx={{ color: bulkEditSelection.size === 0 && isSubmitted ? "error.main" : "text.secondary" }}
        />
      }
      inputProps={{ "aria-label": isSelected ? "Fahrstunde abwählen" : "Fahrstunde auswählen" }}
      onClick={(event) => event.stopPropagation()}
      onChange={() => setBulkEditSelection((selectedIds) => _toggleBulkEditSelection(selectedIds, drivingLesson))}
    />
  );
}

interface _BulkEditFormValues {
  instructorId: string;
  drivingLicenseClass: "__DO_NOT_CHANGE__" | DrivingLicenseClass;
  drivingLessonType: "__DO_NOT_CHANGE__" | DrivingLessonType;
  carId: string;
  motorcycleId: string;
  secondMotorcycleOrCarId: string;
  trailerId: string;
  startingPointId: string;
}

const _bulkEditFormDefaultValues: _BulkEditFormValues = {
  instructorId: "__DO_NOT_CHANGE__",
  drivingLicenseClass: "__DO_NOT_CHANGE__",
  drivingLessonType: "__DO_NOT_CHANGE__",
  carId: "__DO_NOT_CHANGE__",
  motorcycleId: "__DO_NOT_CHANGE__",
  secondMotorcycleOrCarId: "__DO_NOT_CHANGE__",
  trailerId: "__DO_NOT_CHANGE__",
  startingPointId: "__DO_NOT_CHANGE__",
};

function _BulkEditForm(props: { student: Student; cancel: () => void }) {
  const { student, cancel } = props;
  const bulkEditSelection = useRecoilValue(_bulkEditSelectionAtom);
  const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const bookedTrainingsIds = new Set(bulkEditSelection.values());
  const numSelectedDrivingLessons = bulkEditSelection.size;
  const {
    formState: { isSubmitting, isSubmitted },
    handleSubmit,
  } = useFormContext<_BulkEditFormValues>();
  const selectedDrivingLicenseClass = useWatch<_BulkEditFormValues>({ name: "drivingLicenseClass" });
  let drivingLicenseClass_: DrivingLicenseClass | undefined;
  if (selectedDrivingLicenseClass && selectedDrivingLicenseClass !== "__DO_NOT_CHANGE__") {
    drivingLicenseClass_ = selectedDrivingLicenseClass as DrivingLicenseClass;
  } else if (bookedTrainingsIds.size === 0) {
    drivingLicenseClass_ = student.activeOrMostRecentBookedTraining.drivingLicenseClass;
  } else if (bookedTrainingsIds.size === 1) {
    const bookedTrainingId = bookedTrainingsIds[Symbol.iterator]().next().value!;
    const bookedTraining = student.bookedTrainingsById[bookedTrainingId];
    drivingLicenseClass_ = bookedTraining?.drivingLicenseClass;
  } else {
    // multiple selected driving lessons with different booked trainings
    // => we cannot determine the driving license class
  }

  const bulkEdit = async (formData: _BulkEditFormValues) => {
    if (numSelectedDrivingLessons === 0) {
      showErrorToast("Bitte wähle die Fahrstunden aus, die geändert werden sollen.");
      return;
    }
    const {
      instructorId,
      drivingLicenseClass,
      drivingLessonType,
      startingPointId,
      carId,
      motorcycleId,
      secondMotorcycleOrCarId,
      trailerId,
    } = formData;
    if (drivingLicenseClass !== selectedDrivingLicenseClass) {
      throw new Error(
        `Should never happen: drivingLicenseClass !== selectedDrivingLicenseClass -- drivingLicenseClass: ${drivingLicenseClass}, selectedDrivingLicenseClass: ${selectedDrivingLicenseClass}`,
      );
    }
    if (
      instructorId === "__DO_NOT_CHANGE__" &&
      drivingLicenseClass === "__DO_NOT_CHANGE__" &&
      drivingLessonType === "__DO_NOT_CHANGE__" &&
      startingPointId === "__DO_NOT_CHANGE__"
    ) {
      if (
        !drivingLicenseClass_ ||
        (isForMotorcycle(drivingLicenseClass_) &&
          motorcycleId === "__DO_NOT_CHANGE__" &&
          secondMotorcycleOrCarId === "__DO_NOT_CHANGE__") ||
        (isForCarWithTrailer(drivingLicenseClass_) &&
          carId === "__DO_NOT_CHANGE__" &&
          trailerId === "__DO_NOT_CHANGE__") ||
        (isForCarOnly(drivingLicenseClass_) && carId === "__DO_NOT_CHANGE__")
      ) {
        showErrorToast("Bitte wähle aus, was geändert werden soll.");
        return;
      }
    }
    const editDto: z.infer<typeof schemas.EditCalendarEventDto3> = {
      type: "DrivingLesson",
      ...(instructorId === "__DO_NOT_CHANGE__" ? {} : { instructorId }),
      ...(drivingLicenseClass === "__DO_NOT_CHANGE__"
        ? {}
        : {
            bookedTrainingId: (() => {
              const bookedTraining = student.bookedTrainings.find(
                (it) => it.drivingLicenseClass === drivingLicenseClass,
              );
              if (!bookedTraining) {
                throw new Error(
                  `Student ${student.id} has no booked training for driving license class ${drivingLicenseClass}`,
                );
              }
              return bookedTraining.id;
            })(),
          }),
      ...(drivingLessonType === "__DO_NOT_CHANGE__" ? {} : { drivingLessonType }),
      ...(startingPointId === "__DO_NOT_CHANGE__"
        ? {}
        : {
            location: await (async () => {
              const {
                data: { postalAddress },
              } = await startingPointsProvider.getOne("ignored", { id: startingPointId });
              return _pick(postalAddress, ["street", "postalCode", "city"]);
            })(),
          }),
      ...(drivingLicenseClass_ && isForCar(drivingLicenseClass_) && carId !== "__DO_NOT_CHANGE__"
        ? { vehicleId: carId }
        : {}),
      ...(drivingLicenseClass_ && isForCarWithTrailer(drivingLicenseClass_) && trailerId !== "__DO_NOT_CHANGE__"
        ? { vehicle2Id: trailerId }
        : {}),
      ...(drivingLicenseClass_ && isForMotorcycle(drivingLicenseClass_) && motorcycleId !== "__DO_NOT_CHANGE__"
        ? { vehicleId: motorcycleId }
        : {}),
      ...(drivingLicenseClass_ &&
      isForMotorcycle(drivingLicenseClass_) &&
      secondMotorcycleOrCarId !== "__DO_NOT_CHANGE__"
        ? { vehicle2Id: secondMotorcycleOrCarId }
        : {}),
    };
    await runSafely(
      async () => {
        const serverClient = await getAuthenticatedServerClient();
        let numChangedDrivingLessons = 0;
        for (const id of bulkEditSelection.keys()) {
          try {
            await serverClient.editEvent_v3(editDto, { params: { eventId: id } });
            ++numChangedDrivingLessons;
          } catch (error) {
            console.error("Error during bulk edit", error);
            if (numChangedDrivingLessons === 0) {
              throw error;
            } else {
              throw new LocalizedError(
                numChangedDrivingLessons === 1
                  ? `Es wurde nur eine von ${numSelectedDrivingLessons} Fahrstunden geändert.`
                  : `Es wurden nur ${numChangedDrivingLessons} von ${numSelectedDrivingLessons} Fahrstunden geändert.`,
              );
            }
          }
        }
      },
      {
        successMessage: "Die ausgewählten Fahrstunden wurden erfolgreich geändert.",
        onSuccess: cancel,
      },
    );
  };

  return (
    <Card sx={{ ...overviewCardStyle, paddingTop: "20px" }}>
      <form onSubmit={handleSubmit(bulkEdit)}>
        <Column gap={2}>
          <Row justifyContent="space-between" alignItems="center" gap={1}>
            <Typography component="h2" sx={{ fontWeight: "bold", fontSize: "19px" }}>
              {"Mehrere Termine bearbeiten: "}
              {numSelectedDrivingLessons === 0 ? (
                <span className={isSubmitted ? "error" : undefined}> Wähle Fahrstunden</span>
              ) : null}
              {numSelectedDrivingLessons > 0 && (
                <span>{`${numSelectedDrivingLessons}\u00A0${numSelectedDrivingLessons === 1 ? "Fahrstunde" : "Fahrstunden"} ausgewählt`}</span>
              )}
            </Typography>
            <IconButton onClick={cancel} aria-label="Mehrfachbearbeitung schließen">
              <CloseIcon color="action" />
            </IconButton>
          </Row>
          <Alert severity="info">
            Wähle die Fahrstunden aus, die du gemeinsam bearbeiten möchtest. Änderungen gelten nur für deine Auswahl.
          </Alert>
          <Collapse in={numSelectedDrivingLessons > 0} timeout={prefersReducedMotion ? 0 : 220} unmountOnExit>
            <Column gap={2}>
              <InstructorInput source="instructorId" bulkEdit size="small" />
              <DrivingLicenseClassInput
                student={student}
                source="drivingLicenseClass"
                bulkEdit
                label="Ausbildung für Führerscheinklasse"
                size="small"
              />
              <DrivingLessonTypeInput source="drivingLessonType" bulkEdit size="small" />
              {drivingLicenseClass_ ? (
                <VehiclesInput drivingLicenseClass={drivingLicenseClass_} bulkEdit size="small" />
              ) : (
                <>
                  <HiddenInput source="carId" value="__DO_NOT_CHANGE__" />
                  <HiddenInput source="motorcycleId" value="__DO_NOT_CHANGE__" />
                  <HiddenInput source="secondMotorcycleOrCarId" value="__DO_NOT_CHANGE__" />
                  <HiddenInput source="trailerId" value="__DO_NOT_CHANGE__" />
                </>
              )}
              <StartingPointInput source="startingPointId" bulkEdit student={student} size="small" />
              <Row justifyContent="end" sx={{ mt: 1 }}>
                <LoadingButton variant="contained" type="submit" loading={isSubmitting}>
                  Speichern
                </LoadingButton>
              </Row>
            </Column>
          </Collapse>
        </Column>
      </form>
    </Card>
  );
}
